import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, User, ShieldCheck, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("login"); // login | otp
  const [pendingToken, setPendingToken] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { username });
      toast.success(data.message || "If that account exists, a code has been sent");
      setCode(""); setStep("reset");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send code");
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== newPw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { username, code, new_password: newPw });
      toast.success("Password changed — please sign in");
      setPassword(""); setNewPw(""); setNewPw2(""); setCode(""); setStep("login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user) navigate("/");
    if (params.get("expired")) toast.error("Session expired. Please log in again.");
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.otp_required) {
        setPendingToken(data.pending_token);
        setStep("otp");
        toast.success("OTP sent to your registered mobile number");
      } else {
        login(data.token, data.user);
        navigate("/");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { pending_token: pendingToken, code });
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(135deg, #eef2fb 0%, #cfd9f2 100%)" }}
      />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#4e73df]/15 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#4e73df]/10 blur-3xl" />

      <div className="relative w-full max-w-md gm-fade-up">
        <div className="text-center mb-6">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-white border border-[#dfe4fb] items-center justify-center text-[#5b6ef5] gm-shadow-lg mb-4">
            <Zap className="w-8 h-8 fill-[#5b6ef5]" />
          </div>
          <h1 className="text-2xl font-bold text-[#3a3b45]">Glitch Manager</h1>
          <p className="text-slate-500 text-sm mt-1">Manage guest incidents</p>
        </div>

        <div className="bg-white rounded-2xl gm-shadow-lg p-7">
          {step === "forgot" ? (
            <form onSubmit={sendReset} className="space-y-4" data-testid="forgot-form">
              <p className="text-sm text-slate-500">Enter your username to receive an SMS reset code.</p>
              <input className="w-full pl-3 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" required data-testid="forgot-username" />
              <button disabled={loading} className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="forgot-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Send code</button>
              <button type="button" onClick={() => setStep("login")} className="w-full text-sm text-slate-500 hover:text-[#4e73df]">Back to sign in</button>
            </form>
          ) : step === "reset" ? (
            <form onSubmit={doReset} className="space-y-3" data-testid="reset-form">
              <p className="text-sm text-slate-500">Enter the code sent to your phone and choose a new password.</p>
              <input className="w-full pl-3 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" maxLength={6} required data-testid="reset-code" />
              <input type="password" autoComplete="new-password" className="w-full pl-3 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" required />
              <input type="password" autoComplete="new-password" className="w-full pl-3 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} placeholder="Confirm new password" required />
              <button disabled={loading} className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Change password</button>
              <button type="button" onClick={() => setStep("login")} className="w-full text-sm text-slate-500 hover:text-[#4e73df]">Back to sign in</button>
            </form>
          ) : step === "login" ? (
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Username</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="login-username"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-1.5 block">Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="login-password"
                    type="password"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>
              <button
                data-testid="login-submit"
                disabled={loading}
                className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg transition-all hover:-translate-y-0.5 gm-shadow flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Sign In
              </button>
              <button type="button" onClick={() => setStep("forgot")} className="w-full text-sm text-slate-500 hover:text-[#4e73df]" data-testid="forgot-link">Forgot password?</button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4" data-testid="otp-form">
              <div className="text-center mb-2">
                <ShieldCheck className="w-10 h-10 text-[#4e73df] mx-auto mb-2" />
                <p className="text-sm text-slate-500">Enter the 6-digit code sent to your phone</p>
              </div>
              <input
                data-testid="otp-code"
                className="w-full text-center tracking-[0.5em] text-lg font-semibold py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="______"
                maxLength={6}
                required
              />
              <button
                data-testid="otp-submit"
                disabled={loading}
                className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Verify & Sign In
              </button>
              <button
                type="button"
                onClick={() => setStep("login")}
                className="w-full text-sm text-slate-500 hover:text-[#4e73df]"
              >
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
